import { useState, useEffect, useMemo, useCallback } from "react";
import { Query, Predicate } from "@syncfusion/ej2-data";
import { useBaseODataManager } from "../../../../stores/OData/useBaseODataManager";
import { ODataCheckboxFieldProps } from "./ODataCheckboxField";
import { ODataCheckboxFieldValue } from "../../types/fieldTypes";

export const useODataCheckboxField = (props: ODataCheckboxFieldProps) => {
  const [localValues, setLocalValues] = useState<ODataCheckboxFieldValue>(
    props.value || []
  );
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [allOptions, setAllOptions] = useState<
    Array<{ label: string; value: string | number }>
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [allRecordsLoaded, setAllRecordsLoaded] = useState<boolean>(false);

  // Use either provided dataManager or create one from useBaseODataManagerProps
  const dataManager =
    props.dataManager ?? useBaseODataManager(props.odataProps!).dataManager;

  // Update local values when props change
  useEffect(() => {
    setLocalValues(props.value || []);
  }, [props.value]);

  // Memoize additional conditions to prevent unnecessary re-renders
  const memoizedAdditionalConditions = useMemo(() => {
    return props.additionalConditions;
  }, [JSON.stringify(props.additionalConditions)]);

  // Fetch distinct values from OData
  const fetchDistinctValues = useCallback(
    async (
      page: number = 0,
      searchFilter?: string
    ): Promise<Array<{ label: string; value: string | number }>> => {
      if (!dataManager || !props.fieldName) {
        return [];
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build the query with distinct values
        let query = new Query().distinct(props.fieldName);
        // Add search filter if provided
        if (searchFilter && searchFilter.trim()) {
          const searchPredicate = new Predicate(
            props.fieldName,
            "contains",
            searchFilter.trim(),
            true
          );
          query = query.where(searchPredicate);
        }

        // Add additional conditions if provided
        if (
          memoizedAdditionalConditions &&
          memoizedAdditionalConditions.length > 0
        ) {
          let combinedPredicate = memoizedAdditionalConditions[0];
          for (let i = 1; i < memoizedAdditionalConditions.length; i++) {
            combinedPredicate = combinedPredicate.and(
              memoizedAdditionalConditions[i]
            );
          }
          query = query.where(combinedPredicate);
        }

        // Add sorting
        if (props.sortBy) {
          query = query.sortBy(props.sortBy, props.sortOrder || "ascending");
        } else {
          query = query.sortBy(props.fieldName, "ascending");
        }

        // Add pagination
        const pageSize = props.pageSize || 50;
        const skip = page * pageSize;
        query = query.skip(skip).take(pageSize).requiresCount();

        // Execute the query
        const result = await dataManager.executeQuery(query);
        const data = (result as any).result || [];
        const count = (result as any).count || 0;

        // Update total count for hasMore calculation
        setTotalCount(count);

        // Transform data to options format
        return data
          .map((item: any) => {
            const value = item[props.fieldName];
            if (value === null || value === undefined) {
              return null;
            }
            return {
              label: String(value),
              value: value,
            };
          })
          .filter((option: any) => option !== null);
      } catch (err) {
        console.error("Error:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [
      dataManager,
      props.fieldName,
      memoizedAdditionalConditions,
      props.sortBy,
      props.sortOrder,
      props.pageSize,
    ]
  );

  // Load data when dataManager, fieldName, or searchText changes
  useEffect(() => {
    if (dataManager && props.fieldName) {
      const timeoutId = setTimeout(
        () => {
          fetchDistinctValues(0, searchText).then((options) => {
            setAllOptions(options);
            setCurrentPage(0);
            setHasSearched(!!searchText.trim());
            // Reset allRecordsLoaded when searching or changing field
            setAllRecordsLoaded(false);
          });
        },
        searchText ? 300 : 0
      ); // Debounce only for search, not initial load

      return () => clearTimeout(timeoutId);
    }
  }, [dataManager, props.fieldName, searchText, fetchDistinctValues]);

  // Check if all records are loaded after initial load (only when not searching)
  useEffect(() => {
    if (!searchText.trim() && totalCount > 0 && allOptions.length > 0) {
      if (allOptions.length >= totalCount) {
        setAllRecordsLoaded(true);
      }
    }
  }, [allOptions.length, totalCount, searchText]);

  // Filter options based on search text, but keep selected items visible
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) {
      return allOptions;
    }

    const searchLower = searchText.toLowerCase();
    const matchingOptions = allOptions.filter((option) =>
      option.label.toLowerCase().includes(searchLower)
    );

    // Add selected items that don't match the search criteria
    const selectedItemsNotMatching = (localValues || [])
      .map((selectedValue) => {
        // Check if this selected value is already in matchingOptions
        const alreadyInMatching = matchingOptions.some(
          (option) => option.value === selectedValue
        );

        if (alreadyInMatching) {
          return null; // Already included in matching options
        }

        // Check if this selected value exists in allOptions
        const existingOption = allOptions.find(
          (option) => option.value === selectedValue
        );

        if (existingOption) {
          return { ...existingOption, isSelectedButNotMatching: true };
        }

        // If the selected value doesn't exist in allOptions, create a placeholder
        return {
          label: String(selectedValue),
          value: selectedValue,
          isSelectedButNotMatching: true,
        };
      })
      .filter((item) => item !== null);

    return [...matchingOptions, ...selectedItemsNotMatching];
  }, [allOptions, searchText, localValues]);

  // Load more data
  const loadMore = useCallback(async () => {
    if (!dataManager || !props.fieldName || isLoading) {
      return;
    }

    const nextPage = currentPage + 1;
    const newOptions = await fetchDistinctValues(nextPage, searchText);

    if (newOptions.length > 0) {
      setAllOptions((prev) => [...prev, ...newOptions]);
      setCurrentPage(nextPage);

      // Check if we've loaded all records (only when not searching)
      if (!searchText.trim() && totalCount > 0) {
        const totalLoaded = allOptions.length + newOptions.length;
        if (totalLoaded >= totalCount) {
          setAllRecordsLoaded(true);
        }
      }
    }
  }, [
    dataManager,
    props.fieldName,
    currentPage,
    searchText,
    isLoading,
    fetchDistinctValues,
    allOptions.length,
    totalCount,
  ]);

  // Check if there are more items to load
  const hasMore = useMemo(() => {
    const pageSize = props.pageSize || 50;
    // If we have a total count from the server, use that
    if (totalCount > 0) {
      return allOptions.length < totalCount;
    }
    // Fallback to the old logic if no total count available
    return allOptions.length >= (currentPage + 1) * pageSize;
  }, [allOptions.length, currentPage, props.pageSize, totalCount]);

  return {
    localValues,
    setLocalValues,
    availableOptions: allOptions,
    filteredOptions,
    searchText,
    setSearchText,
    hasMore,
    loadMore,
    isLoading,
    error,
    hasSearched,
    allRecordsLoaded,
  };
};
